import { useQuery, UseQueryResult } from 'react-query';
import { Countries, Regions } from 'src/graphql/schema.graphql';
import {
  TranslatedItem,
  useTranslateItem,
} from 'src/hooks/directus/useTranslateItem';
import { useAPIs } from 'src/hooks/useAPIs';
import { APIs } from 'src/models/apis';
import { QueryKey } from 'src/models/query';
import { speakersRegionsGqlQuery } from 'src/modules/experts/graphql/speakers-regions.query';
import { longStaleQueryBase } from 'src/utils/queryCommonOptions';

export const fetchSpeakersRegions = async (apis: APIs) => {
  const directus = await apis.directus();

  return directus
    .query(speakersRegionsGqlQuery)
    .then(({ regions, countries }) => {
      return regions.map((region) => ({
        ...region,
        countries: countries
          .filter((c) =>
            c.regions.some((r) => r.regions_id.slug === region.slug),
          )
          .map((c) => ({
            countries_id: { slug: c.slug, translations: c.translations },
          })),
      }));
    });
};

export const fetchSpeakersRegionsQuery = (apis: APIs) => ({
  queryKey: QueryKey.SpeakersRegions,
  queryFn: () => fetchSpeakersRegions(apis),
  ...longStaleQueryBase,
});

export const useSpeakersRegionsQuery = (): UseQueryResult<
  (TranslatedItem<Omit<Regions, 'countries'>> & {
    countries: TranslatedItem<Countries>[];
  })[]
> => {
  const apis = useAPIs();
  const translateItem = useTranslateItem();

  return useQuery({
    ...fetchSpeakersRegionsQuery(apis),
    select: (data: Regions[]) =>
      data.map((region) => ({
        ...translateItem(region),
        countries: region.countries.map((c) => translateItem(c.countries_id)),
      })),
  });
};
